import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Switch,
  Modal,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import WalletForm from "./WalletForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import moment from "moment-timezone";
import {
  addWallet,
  deleteWallet,
  getWallet,
  updateWallet,
  updateWalletStatus,
} from "../../store/actions/wallet.js";
import Swal from "sweetalert2";
import { getCustomers } from "../../store/actions/customers";
import { getPackage } from "../../store/actions/package";

const { Title } = Typography;

const Wallet = () => {
  document.title = "Wallet";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const walletReducer = useSelector((state) => state.wallet.list);
  const businessType = useSelector((state) => state.businessType.list);
  const customers = useSelector((state) => state.customers.list);
  const packages = useSelector((state) => state.packageReducer.list);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await dispatch(getWallet());
        // await dispatch(getBusinessType());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getPackage());
  }, [dispatch]);

  const handleCreate = async (values) => {
    if (selectedRecord) {
      const updateData = {
        id: selectedRecord.id,
        ...values,
      };
      await dispatch(updateWallet(updateData));
      await dispatch(getWallet());
    } else {
      await dispatch(addWallet(values));
      await dispatch(getWallet());
    }
    setIsModalVisible(false);
  };

  const handleDelete = async (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteWallet(item.id));
      }
    });
  };

  const confirmDelete = async () => {
    try {
      await dispatch(deleteWallet(itemToDelete.id));
      await dispatch(getWallet());
      setDeleteModalVisible(false);
    } catch (error) {
      message.error("An error occurred while deleting the item");
    }
  };

  const handleChangeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1; // Toggle status
    const updatedData = { id: id, status: newStatus }; // Create an object for update
    await dispatch(updateWalletStatus(updatedData));
    await dispatch(getWallet());
  };

  const renderCell = (text) => (text ? text : "N/A");
  let columns = [
    {
      title: "Customer Name",
      dataIndex: "customername",
      key: "customername",
      sorter: stringSorter("customername"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Package Name",
      dataIndex: "packagename",
      key: "packagename",
      sorter: stringSorter("packagename"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Amount",
      dataIndex: "amt",
      key: "amt",
      sorter: stringSorter("amt"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: stringSorter("type"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Payment Type",
      dataIndex: "payType",
      key: "payType",
      sorter: stringSorter("payType"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Payment Notes",
      dataIndex: "payNotes",
      key: "payNotes",
      sorter: stringSorter("payNotes"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: stringSorter("description"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD HH:mm:ss")
          : "",
      sorter: (a, b) =>
        moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
          ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      sorter: stringSorter("createdByName"),
      sortDirections: ["ascend", "descend"],
      render: renderCell,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) =>
        moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD HH:mm:ss")
          : "",
      sorter: (a, b) =>
        moment(a.updatedAt).isValid() && moment(b.updatedAt).isValid()
          ? moment(a.updatedAt).unix() - moment(b.updatedAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Updated By",
      dataIndex: "updatedByName",
      key: "updatedByName",
      sorter: stringSorter("updatedByName"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Switch
          checked={text === 1}
          onChange={() => handleChangeStatus(record.id, text)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setSelectedRecord(record);
              setIsModalVisible(true);
            }}
          >
            <EditOutlined />
          </Button>

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          ></Button>
        </Space>
      ),
    },
  ];
  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Wallet</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Wallet
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Wallet
              </Button>
            </Col>
          </Row>
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={walletReducer.map((item) => ({
                ...item,
                key: item.id,
                status: item.status ? 1 : 0,
              }))}
              columns={columns}
              filterBy={[
                {
                  field: "cusId",
                  fieldType: "SELECT",
                  options: customers,
                  filter: "id",
                  label: "customer",
                },
                {
                  field: "pId",
                  fieldType: "SELECT",
                  options: packages,
                  filter: "id",
                  label: "packages",
                },
                {
                  field: "type",
                  fieldType: "SELECT",
                  options: [
                    { id: "CREDIT", name: "CREDIT" },
                    { id: "DEBIT", name: "DEBIT" },
                  ],
                  filter: "id",
                  label: "type",
                },
                {
                  field: "payType",
                  fieldType: "SELECT",
                  options: [
                    { id: "Razorpay", name: "Razorpay" },
                    { id: "Cash", name: "Cash" },
                    { id: "Bank", name: "Bank" },
                    { id: "NEFT", name: "NEFT" },
                    { id: "UPI", name: "UPI" },
                  ],
                  filter: "id",
                  label: "payment type",
                },
              ]}
              isActive={true}
            />
          )}
        </Space>
      </Card>
      <WalletForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        record={selectedRecord}
        businessType={businessType}
      />
      <Modal
        title="Confirm Delete"
        zIndex={100000000}
        visible={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="delete" danger onClick={confirmDelete}>
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to delete this item?</p>
        {itemToDelete && (
          <p>
            <strong>Record Name:</strong> {itemToDelete.name}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default Wallet;
